import type { SubjectRate } from '@/services/api/attendance/attendance';
import type { StudentSummary } from '@/services/api/student-summary';
import { fetchApi } from '@/services/api-client';

export const ADMIN_LESSON_EXCUSALS_PATH = '/admin/lesson-excusals';

export type LessonExcusalType = 'injury' | 'illness' | 'medical' | 'religious' | 'other';

export type LessonExcusal = {
  id: string;
  studentId: string;
  student: StudentSummary | null;
  submittedByParentId: string | null;
  submittedByParentName: string | null;
  date: string;
  subjectId: string;
  subjectName: string;
  classId: string;
  periodId: string;
  periodName: string;
  type: LessonExcusalType;
  reason: string;
  /** Who put the excusal on file, and when — null when a family reported it themselves. */
  reviewerId: string | null;
  reviewedAt: string | null;
  /** Whole percent (0–100) the parent was shown when they submitted; null when not applicable. */
  rateAtRequest: number | null;
  createdAt: string;
  /** Where the student's rate for this subject stands right now — not necessarily what the
   *  parent saw at `rateAtRequest`. */
  currentRate: SubjectRate | null;
};

/** One excusable lesson a student has on a chosen day, with the rate the form warns from. */
export type LessonExcusalOption = {
  periodId: string;
  periodName: string;
  startTime: string;
  endTime: string;
  subjectId: string;
  subjectName: string;
  classId: string;
  rate: SubjectRate;
};

/** Staff recording an excusal a family reported off-platform. */
export type AdminCreateLessonExcusalData = {
  studentId: string;
  date: string;
  periodId: string;
  type: LessonExcusalType;
  reason: string;
};

/** The write endpoints return the plain excusal — they do not recompute `currentRate`. */
export type WrittenLessonExcusal = Omit<LessonExcusal, 'currentRate'>;

export const adminLessonExcusalsApi = {
  list: (params: { studentId?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.studentId) qs.append('studentId', params.studentId);
    const s = qs.toString();
    return fetchApi<LessonExcusal[]>(`${ADMIN_LESSON_EXCUSALS_PATH}${s ? `?${s}` : ''}`);
  },
  options: (studentId: string, date: string) =>
    fetchApi<LessonExcusalOption[]>(
      `${ADMIN_LESSON_EXCUSALS_PATH}/options?studentId=${encodeURIComponent(studentId)}&date=${encodeURIComponent(date)}`
    ),
  detail: (id: string) => fetchApi<LessonExcusal>(`${ADMIN_LESSON_EXCUSALS_PATH}/${id}`),
  create: (data: AdminCreateLessonExcusalData) =>
    fetchApi<WrittenLessonExcusal>(ADMIN_LESSON_EXCUSALS_PATH, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  // The undo for any excusal, whoever reported it: nothing waits to be approved.
  remove: (id: string) => fetchApi<void>(`${ADMIN_LESSON_EXCUSALS_PATH}/${id}`, { method: 'DELETE' }),
};
